import React, { useState, useEffect } from 'react';
import MiniSpinner from "../General/MiniSpinner";
import { getUserTypes } from "../../actions/usuariosActions";
import { getSidebarPermissionItems } from "../../actions/sidebarActions";
import PropTypes from 'prop-types';

const getItemsIds = (items = []) => {
  return items.flatMap(item => [
    item.id,
    ...(item.childItems ? getItemsIds(item.childItems) : [])
  ]);
};

export default function UserSidebarItems({
  tipoUsuario,
  previousHiddenItems = [],
  updateHiddenItems
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [sidebarItems, setSidebarItems] = useState([]);
  const [listAllIds, setListAllIds] = useState([]);
  const [hiddenItems, setHiddenItems] = useState(previousHiddenItems);
  const [isAllVisible, setIsAllVisible] = useState(previousHiddenItems?.length === 0);

    useEffect(() => {
        const userType = getUserTypes(tipoUsuario);
        const userItems = getSidebarPermissionItems(userType);
        setSidebarItems(userItems);
        setListAllIds(getItemsIds(userItems));

        setIsLoading(false);
    }, [tipoUsuario, previousHiddenItems]);

  useEffect(() => {
    setHiddenItems(previousHiddenItems);
  }, [previousHiddenItems]);

  useEffect(() => {
    setIsAllVisible(hiddenItems?.length === 0);
    // Solo llamar a updateHiddenItems si hiddenItems cambió y es diferente al prop, pero evitar ciclo infinito
    // No dependas de previousHiddenItems aquí para evitar el loop
    if (updateHiddenItems && typeof updateHiddenItems === 'function') {
      if (JSON.stringify(hiddenItems) !== JSON.stringify(previousHiddenItems)) {
        updateHiddenItems(hiddenItems);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hiddenItems, updateHiddenItems]);

  const handleToggleAllCheckbox = (e) => {
    e.preventDefault();
    setHiddenItems(isAllVisible ? listAllIds : []);
  };

  const handleToggleCheckboxs = (id, isHidden, parentId) => {
    setHiddenItems(prevHiddenItems => {
      let updatedHiddenItems = toggleHiddenItem(id, isHidden, prevHiddenItems);
      const parentItem = sidebarItems.find(item => item.id === (parentId ?? id));

      if (parentItem?.childItems) {
        const childIds = parentItem.childItems.map(childItem => childItem.id);
        const isParentClicked = parentId === undefined;

        if (isParentClicked) {
          childIds.forEach(childId => {
            updatedHiddenItems = toggleHiddenItem(childId, isHidden, updatedHiddenItems);
          });
        } else {
          const isHiddenEveryChild = childIds.every(childId => updatedHiddenItems.includes(childId));
          updatedHiddenItems = toggleHiddenItem(parentId, !isHiddenEveryChild, updatedHiddenItems);
        }
      }

      return updatedHiddenItems;
    })
  };

  const toggleHiddenItem = (itemId, isHidden, currentHiddenItems) => {
    return isHidden ? currentHiddenItems.filter(id => id !== itemId) : [...currentHiddenItems, itemId];
  };

  const renderItem = (item, parentId) => {
    const { id, icon, label, childItems } = item;
    const isHidden = hiddenItems?.includes(id);
    const isChild = parentId !== undefined;
    const isChecked = !isHidden;

    return (
      <div className="col-12" key={id} style={{ marginBottom: '2px' }}>
        <div
          className={`d-flex align-items-center px-2 py-1 rounded ${isChild ? 'ms-3 border-start border-2 border-light ps-2' : 'bg-light'}`}
          style={{ transition: 'background-color 0.2s', minHeight: '38px' }}
        >
          <div className="form-check form-switch mb-0 flex-grow-1 d-flex align-items-center">
            <input
              className="form-check-input mt-0"
              type="checkbox"
              role="switch"
              id={`switch-${id}`}
              checked={isChecked}
              onChange={() => handleToggleCheckboxs(id, isHidden, parentId)}
              style={{ cursor: 'pointer', width: '2.2em', height: '1.1em' }}
            />
            <label
              className="form-check-label d-flex align-items-center w-100 ms-2"
              htmlFor={`switch-${id}`}
              style={{ cursor: 'pointer', userSelect: 'none' }}
            >
              <i className={`${icon} me-2 ${isChecked ? 'text-primary' : 'text-muted'}`} style={{ fontSize: '1rem' }} />
              <span className={isChecked ? 'text-dark fw-medium' : 'text-muted'} style={{ fontSize: '0.9rem' }}>
                {label}
              </span>
              <span className="ms-auto badge bg-light text-secondary rounded-pill border" style={{ fontSize: '0.65rem', padding: '0.25em 0.6em' }}>
                {isChecked ? 'Visible' : 'Oculto'}
              </span>
            </label>
          </div>
        </div>
        {childItems?.map((child => renderItem(child, id)))}
      </div>
    )
  }


  return (
    <div className="row">

      <div className="col-12 d-flex justify-content-between align-items-center mb-2">
        <p className="text-muted small text-uppercase fw-bold mb-0" style={{ fontSize: '0.75rem', fontWeight: "600" }}>Secciones</p>

        <button
          className={`btn btn-sm ${isAllVisible ? 'btn-outline-secondary' : 'btn-outline-primary'} rounded-pill px-3 py-0`}
          style={{ fontSize: '0.8rem', height: '28px' }}
          onClick={(e) => handleToggleAllCheckbox(e)}
        >
          <i className={`mdi ${isAllVisible ? 'mdi-eye-off-outline' : 'mdi-eye-outline'} me-1`}></i>
          {isAllVisible ? "Ocultar todas" : "Mostrar todas"}
        </button>
      </div>

      <div className="col-12">
        {isLoading ? (
          <div className="d-flex justify-content-center py-3">
            <MiniSpinner />
          </div>
        ) : (
          <div className="row g-0">
            {sidebarItems.map((item) => { return renderItem(item) })}
          </div>
        )}
      </div>

    </div>
  );
}
UserSidebarItems.propTypes = {
    tipoUsuario: PropTypes.number,
    previousHiddenItems: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    updateHiddenItems: PropTypes.func,
}